import { motion } from "framer-motion";
import { Compass, MapPin, Car } from "lucide-react";

const STEPS = [
  {
    num: "01",
    icon: Compass,
    label: "Scan",
    title: "Read the sun in real time",
    body: "Open ShadyParking and it reads the sun's position and your car's heading on the spot, telling you exactly which side of the cabin stays shaded right now.",
    img: "/app-shots/01-live-shade-status.jpeg",
    alt: "ShadyParking home screen showing the driver's side is shaded with a live sun compass",
  },
  {
    num: "02",
    icon: MapPin,
    label: "Pick a zone",
    title: "Choose a shaded heading",
    body: "Get ranked parking headings, each showing how long your seat keeps its shade. Just point your car the way the top spot suggests.",
    img: "/app-shots/03-park-spot-recommendations.jpeg",
    alt: "ShadyParking recommendations list ranking parking headings by shade duration",
  },
  {
    num: "03",
    icon: Car,
    label: "Park in the shade",
    title: "Step back into a cool car",
    body: "See your best shade window at a glance — \u201cshaded now for 1h\u201d — so you park with confidence and come back to a cabin that stayed cool.",
    img: "/app-shots/04-best-shade-window.jpeg",
    alt: "ShadyParking best shade window card showing the driver side stays shaded until 1:00 PM",
  },
];

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works-section"
      className="border-b border-[var(--sp-line)] bg-[var(--sp-bg)]"
    >
      <div className="mx-auto max-w-[1500px] px-5 py-20 sm:px-8 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7 }}
              className="mb-4 flex items-center gap-3 font-mono2 text-xs uppercase tracking-[0.28em] text-[var(--sun)]"
            >
              <span className="h-px w-10 bg-[var(--sun)]" />
              Currently Working // 03
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="max-w-2xl font-display text-2xl font-extrabold uppercase leading-none tracking-tight sm:text-4xl lg:text-5xl"
            >
              From glare to shade in three taps
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-sm text-sm leading-relaxed text-[var(--sp-muted)]"
          >
            Real screens from the live app. No account, no sign-up — open it, and the shade
            engine does the rest in seconds.
          </motion.p>
        </div>

        <div className="mt-14 grid grid-cols-1 gap-px border border-[var(--sp-line)] bg-[var(--sp-line)] lg:grid-cols-3">
          {STEPS.map((s, i) => (
            <motion.article
              key={s.num}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`how-it-works-step-${s.num}`}
              className="group flex flex-col bg-[var(--sp-bg)]"
            >
              <div className="flex items-center justify-between border-b border-[var(--sp-line)] px-8 py-5">
                <span className="flex items-center gap-3">
                  <span className="font-display text-2xl font-black leading-none text-[var(--sun)]">{s.num}</span>
                  <span className="font-mono2 text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--sp-muted)]">
                    {s.label}
                  </span>
                </span>
                <s.icon className="h-5 w-5 text-[var(--sun)]" />
              </div>
              <div className="flex items-center justify-center px-6 pb-2 pt-12 sm:px-8">
                <span className="app-screen w-full max-w-[300px]">
                  <img
                    src={s.img}
                    alt={s.alt}
                    loading="lazy"
                    className="aspect-[4/5] w-full object-contain transition-transform duration-700 group-hover:-translate-y-1"
                  />
                </span>
              </div>
              <div className="p-8">
                <h3 className="font-display text-xl font-bold uppercase tracking-tight sm:text-2xl">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[var(--sp-muted)]">{s.body}</p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
